const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:8000/api/v1";

type GetTokenFn = () => Promise<string>;

let _getAccessToken: GetTokenFn | null = null;

export function setAccessTokenGetter(fn: GetTokenFn) {
  _getAccessToken = fn;
}

async function authHeaders(): Promise<HeadersInit> {
  if (_getAccessToken) {
    try {
      const token = await _getAccessToken();
      return { Authorization: `Bearer ${token}` };
    } catch (err) {
      console.error("Failed to acquire access token:", err);
    }
  }
  const devToken = process.env.REACT_APP_DEV_TOKEN;
  if (devToken) {
    return { Authorization: `Bearer ${devToken}` };
  }
  return {};
}

async function readErrorDetail(res: Response): Promise<string | null> {
  try {
    const body = await res.json();
    if (body && typeof body.detail === "string") return body.detail;
  } catch {
    /* ignore parse error */
  }
  return null;
}

export async function apiGet<T>(path: string): Promise<T> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE_URL}${path}`, { headers });
  if (!res.ok) {
    if (res.status === 401) {
      throw new Error("Your session has expired — please sign out and sign in again.");
    }
    const detail = await readErrorDetail(res);
    if (res.status === 403) {
      // Prefer the backend's own reason. A blanket "assign your account to an
      // organization" here sent people chasing the customer link for 403s that
      // were nothing to do with it — the backend distinguishes "not assigned to
      // a company", "not your company's user" and "internal users only", and
      // swallowing that difference makes the real cause unfindable.
      throw new Error(
        detail ||
          "You're not authorized to view this data. Make sure you're signed in and that your account has been assigned to an organization.",
      );
    }
    throw new Error(
      detail || `GET ${path} failed: ${res.status} ${res.statusText}`,
    );
  }
  return res.json();
}

/** Generic write-side helper. Surfaces FastAPI `detail` strings as the error
 *  message so callers can show them directly to the user. */
export async function apiSend<T>(
  method: "POST" | "DELETE" | "PUT" | "PATCH",
  path: string,
  body?: unknown,
): Promise<T> {
  const headers: Record<string, string> = {
    ...(await authHeaders()) as Record<string, string>,
  };
  if (body !== undefined) headers["Content-Type"] = "application/json";

  const res = await fetch(`${API_BASE_URL}${path}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    if (res.status === 401) {
      throw new Error("Your session has expired — please sign out and sign in again.");
    }
    const detail = await readErrorDetail(res);
    throw new Error(
      detail ||
        `${method} ${path} failed: ${res.status} ${res.statusText}`,
    );
  }
  // 204 No Content has no JSON body.
  if (res.status === 204) return undefined as unknown as T;
  return res.json();
}

export { API_BASE_URL };
